import { useSWR } from '@tiger/swr';
import moment from 'moment';
import { FC } from 'react';
import { FormCheck } from 'react-bootstrap';
import { $Formutil, connect } from 'react-bootstrap-formutil';
import AgreementList from 'app/components/AgreementList';
import Skeleton from 'components/base/Skeleton';
import ErrorBox from 'components/ErrorBox';
import FormGroup from 'components/FormGroup';
import Signature from 'components/forms/TaxForm/Signature';
import type { AgreementItemType, FormCheckType } from 'types/common';
import { AccountCapabilitiesEnum, CategoryEnum, LicenseEnum } from 'types/customer';
import API from 'utils/API';
import { language } from 'utils/i18n';
import styles from './style.scss';

interface AgreementInfoProps {
    $formutil: $Formutil<{
        temporary_agreement_ids_check: boolean;
    }>;
    accountCapability?: AccountCapabilitiesEnum;
    name?: string;
    perms: {
        opt_perm?: FormCheckType;
        treasury_perm?: FormCheckType;
    };
    license?: LicenseEnum;
    category: CategoryEnum;
}

// w8和协议页 日期格式相同，但固定使用英文格式
export const getToday = (needEn?: boolean) =>
    needEn || language !== 'zh_CN' ? moment().format('MM/DD/YYYY') : moment().format('LL');

/**
 *  开户流程协议公共组件
 */
const AgreementInfo: FC<AgreementInfoProps> = ({
    license = LicenseEnum.MARSCO,
    accountCapability,
    perms,
    category,
    name
}) => {
    const { opt_perm, treasury_perm } = perms;
    const products = [Number(opt_perm) === 1 ? 'option' : '', Number(treasury_perm) === 1 ? 'treasury' : '']
        .filter(Boolean)
        .join(',');
    const {
        data: dataAgreements,
        isLoading,
        error,
        mutate
    } = useSWR<AgreementItemType[]>(
        {
            url:
                category === CategoryEnum.IRA
                    ? API.SUPPORT.openIRAAgreements()
                    : API.SUPPORT.openIndividualAgreements(),
            params: {
                account_capabilities: accountCapability,
                products,
                license,
                account_type: 'mo',
                category: category.toLowerCase()
            }
        },
        {
            immutable: true,
            dataChain: res => res.data
        }
    );

    return (
        <div className={styles.root}>
            <h3 className="open-title my-0">{__('GENERAL AGREEMENTS & DISCLOSURES')}</h3>
            <p className={styles.statement}>
                {__(
                    'We are required to provide you with certain disclosures mandated by our regulators. Review the disclosures and agreements, make any required selections and provide any required signatures.'
                )}
            </p>
            {isLoading ? (
                <div className="my-3">
                    <Skeleton line={5} />
                </div>
            ) : error && !dataAgreements ? (
                <ErrorBox error={error} onClick={() => mutate()} />
            ) : (
                <AgreementList className={styles.agreements} agreementsData={dataAgreements} />
            )}
            <p className={styles.description}>
                {__(
                    'BY CLICKING THE "SUBMIT" BUTTON, I ACKNOWLEDGE THAT I HAVE READ AND UNDERSTAND ALL INFORMATION PROVIDED DURING THE ACCOUNT APPLICATION PROCESS, AND THAT TRADEUP SECURITIES, INC. IS ENTITLED TO RELY UPON SUCH INFORMATION; THAT I AGREE TO BE BOUND BY THE TERMS OF THE CUSTOMER AGREEMENT (WHICH CONTAINS A PRE-DISPUTE ARBITRATION CLAUSE DISCLOSED AT THE END OF THE AGREEMENT) AND THE OTHER AGREEMENTS AND DISCLOSURES PROVIDED TO ME ABOVE; AND THAT I UNDERSTAND AND AGREE THAT THE NAME ENTERED BELOW CONSTITUTES MY LEGAL SIGNATURE.'
                )}
            </p>
            {category === CategoryEnum.IRA && (
                <div className={styles.agree}>
                    <p>
                        {__(
                            '本人确认已阅读、理解并同意Account Agreement账户协议中规定的条款，包括Account Agreement第 6 段中的争议前仲裁条款。'
                        )}
                    </p>
                    <p>
                        {__(
                            '我委任 Equity Trust Company 为受托人。在作出此项任命时，我已收到 Application Booklet 开户手册，并同意遵守其中的条款，包括同意通过具有约束力的仲裁解决与 Equity Trust Company 的争议。'
                        )}
                    </p>
                    <p>
                        {__(
                            'TRADEUP SECURITIES, INC. ("TRADEUP") HAS NOT REVIEWED, NOR DO WE ENDORSE OR PROVIDE LEGAL, FINANCIAL, OR ANY OTHER PROFESSIONAL ADVICE REGARDING THE CONTENTS OF THE AGREEMENTS, TERMS, FORMS, NOTICES OR CONDITIONS RELATED TO YOUR IRA ACCOUNT PROVIDED BY EQUITY TRUST COMPANY ("ETC") ("ETC AGREEMENT PACKAGE"). ETC AGREEMENT PACKAGE CONSTITUTES DOCUMENTS SOLELY BETWEEN YOU AND ETC, AND TRADEUP IS NOT A PARTY TO, NOR ASSUMES ANY LIABILITY OR RESPONSIBILITY FOR, SUCH DOCUMENTS. YOU ARE SOLELY RESPONSIBLE FOR REVIEWING THE ETC AGREEMENT PACKAGE AND WE STRONGLY RECOMMEND THAT YOU CONSULT WITH A LEGAL OR FINANCIAL ADVISOR TO ENSURE THAT THEY MEET YOUR NEEDS AND EXPECTATIONS.'
                        )}
                    </p>
                    <p>
                        {__(
                            'BY PROCEEDING, YOU ACKNOWLEDGE AND AGREE THAT YOU REMAIN FULLY SUBJECT TO ALL TERMS, CONDITIONS, AND OBLIGATIONS SET FORTH IN ANY AND ALL AGREEMENTS, DISCLOSURES, OR OTHER GOVERNING DOCUMENTS PREVIOUSLY OR SUBSEQUENTLY ENTERED INTO WITH TRADEUP ("TRADEUP AGREEMENTS"). NOTHING CONTAINED IN THE ETC AGREEMENT PACKAGE MODIFIES, SUPERSEDES, OR LIMITS YOUR OBLIGATIONS UNDER THE TRADEUP AGREEMENTS.'
                        )}
                    </p>
                </div>
            )}
            {/* 跟server确认开户流程不需要显示传递agreement_ids */}
            <FormGroup
                name="temporary_agreement_ids_check"
                checker={value => !!value || __('You need to confirm all the information to submit the application.')}>
                <FormCheck
                    inline
                    type="checkbox"
                    id="temporary_agreement_ids_check"
                    label={__('I agree to the above agreements and disclosures')}
                />
            </FormGroup>
            <Signature name={name || ''} />
        </div>
    );
};

export default connect(AgreementInfo);
